<template>
  <div
    ref="pointer"
    class="cursor"
    >
  </div>
</template>

<script type="text/javascript">
import gsap from 'gsap';
import loop from 'raf-loop';
import Eventer from 'eventer-js';
import { mapGetters } from 'vuex';

import { isMobile, lerp, roundDec } from '@/scripts/utils';


export default {
  name: 'Pointer',

  props: ['isCursorReady'],

  data() {
    return {
      buttons: null,

      mouse: { x: 0, y: 0 },
      interMouse: { x: 0, y: 0 },
      norm: { x: 0, y: 0 },

      magnetism: 5,
      attraction: 30,

      isLerp: true,
      lerp,
      roundDec,
      isMobile,

      oldCtx: null,
      newCtx: null,

      visible: true,
    };
  },

  computed: {
    ...mapGetters({
      stateCtx: 'getCtxButtons',
    }),
  },

  watch: {
    stateCtx(newCtx, oldCtx) {
      if (oldCtx !== null && !this.isMobile()) {
        const els = Array.from(oldCtx.querySelectorAll('[data-mouse]'));
        this.removeButtonsEvents(els);
      }
      if (!this.isMobile()) {
        const els = Array.from(newCtx.querySelectorAll('[data-mouse]'));
        this.addButtonsEvents(els);
      }
    },
  },

  mounted() {
    if (!this.isMobile()) this.eventer = new Eventer(true);

    if (!this.isMobile()) this.buttons = Array.from(document.querySelectorAll('[data-mouse]'));

    if (!this.isMobile()) this.init();
  },

  methods: {
    init() {
      this.bindEvents();

      this.engine = loop(this.renderer.bind(this)).start();
    },

    mousePosition(event) {
      this.mouse = {
        x: event.clientX,
        y: event.clientY,
      };
    },

    bindEvents() {
      window.addEventListener('mousemove', this.mousePosition.bind(this));
      // this.eventer.bindEvent('mousemove', window, this.mousePosition.bind(this));

      this.addButtonsEvents(this.buttons);

      // battery works only chrome
      if ((window.chrome && window.chrome.webstore && !isMobile()) || (window.opr && window.opr.addons && !isMobile())) { // eslint-disable-line
        navigator.getBattery().then((battery) => {
          battery.addEventListener('chargingchange', (status) => {
            let power = true;
            if (!status.target.charging) power = false;
            this.animateBattery(this.$el, power);
          });
        });
      }
    },

    addButtonsEvents(btns) {
      btns.forEach((btn) => {
        const state = btn.getAttribute('data-mouse-attractionState');
        const attraction = btn.getAttribute('data-mouse-attractionVal');

        this.handleMouseMove = this.setButtonPosition.bind(this);

        if (state === 'active' || state === true) this.eventer.bindEvent('mousemove', btn, event => this.handleMouseMove(event, btn, attraction));
        this.eventer.bindEvent('mouseenter', btn, this.setHoverMouse.bind(this, btn));
        this.eventer.bindEvent('mouseleave', btn, this.resetButton.bind(this, btn, state));
      });
    },

    removeButtonsEvents(btns) {
      btns.forEach(btn => this.eventer.clearAll(btn));
    },

    renderer() {
      this.setPos();
    },

    setPos() {
      const lerpedX = lerp(this.mouse.x, this.interMouse.x, 0.75);
      const lerpedY = lerp(this.mouse.y, this.interMouse.y, 0.75);

      this.interMouse.x = this.isLerp ? lerpedX - (this.norm.x / this.magnetism) : this.mouse.x;
      this.interMouse.y = this.isLerp ? lerpedY - (this.norm.y / this.magnetism) : this.mouse.y;

      gsap.set(this.$el, {
        x: this.interMouse.x,
        y: this.interMouse.y,
      });
    },

    setHoverMouse(el) {
      this.$el.setAttribute('data-hover', el.getAttribute('data-hover-type'));
    },

    setButtonPosition(event, el, attraction) {
      this.dim = {
        x: el.clientWidth / 2,
        y: el.clientHeight / 2,
      };

      this.norm = {
        x: roundDec((event.offsetX - this.dim.x) / this.dim.x) * attraction,
        y: roundDec((event.offsetY - this.dim.y) / this.dim.y) * attraction,
      };

      this.animateButton(el);
    },

    animateButton(el) {
      gsap.to(el, 0.5, {
        xPercent: this.norm.x,
        yPercent: this.norm.y,
      });
    },

    animateBattery(el, power) {
      let bounce = 3;

      if (!power) bounce = 0.2;

      gsap.fromTo(el, 3, {
        scale: 1,
      }, {
        scale: bounce,
        ease: gsap.Bounce.easeOut,
        onComplete: () => {
          gsap.to(el, 1, {
            scale: 1,
            clearPros: 'all',
            ease: gsap.Bounce.easeOut,
          });
        },
      });
    },

    resetButton(el, state) {
      this.$el.setAttribute('data-hover', '');

      if (state === 'active' || state) {
        this.norm = { x: 0, y: 0 };
        this.animateButton(el);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/colors";
@import "../application";

.cursor {
  display: none;

  @include mq(sm) {
    position: absolute;
    top: calc(-75px / 2);
    left: calc(-75px / 2);
    width: 75px;
    height: 75px;

    transform-origin: center;
    border-radius: 50%;
    pointer-events: none;

    display: flex;
    align-items: center;
    justify-content: center;

    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;

    border-radius: 50%;
    background-color: $color-blue;

    opacity: 1;
    transform-origin: 50% 50%;
    transform: scale(0.2);
    transition: all 0.3s ease;

    z-index: -2;
  }

  &[data-hover="fadeout"]::after {
    transform: scale(0);
  }

  &.power {
    &::after {
      background: #94ffeb;
      box-shadow: 0px 0px 10px 10px #94ffeb;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Cursor.vue